/**
 * Loads API
 * All load searches go through loads-ingest-service /loads/search endpoint
 */

import axios from 'axios'
import { CanonicalLoad, getLiveSearchSubscriptionPath, stripExternalIdPrefix } from '@numeo/types-core'
import { isSourceErrorCode, isSourceFailureReason, SEARCH_STREAM_PATH, type SourceErrorCode, type SourceFailureReason } from '@numeo/types'
import { getAuthToken, getLoadSearchServiceUrl, loadsIngestServiceAxios } from 'setup/SetupAxios'
import { SearchRequestFailed } from './searchRequestFailed'
import { isRecord } from 'utils/isRecord'
import { normalizeLoadCoordinates } from '@/utils/loadCoordinates'
import { FailedSource, LoadSearchRequest } from '../types/api'

/** Consume one Load Hub search quota. Returns remaining count or null if unlimited. */
export const consumeSearchQuota = async (): Promise<{ allowed: boolean; remaining: number | null }> => {
    try {
        const res = await axios.post<{ success: boolean; remaining?: number }>('/load-radar/load-hub-search-consume')
        return { allowed: true, remaining: res.data.remaining ?? null }
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 429) {
            return { allowed: false, remaining: 0 }
        }
        // Fail-open: allow search if quota check fails
        return { allowed: true, remaining: null }
    }
}

/** Idempotently stop the socket subscription for a closed Load Hub tab. */
export const closeLiveSearchSubscription = async (subscriptionId: string): Promise<void> => {
    await loadsIngestServiceAxios.delete(getLiveSearchSubscriptionPath(subscriptionId))
}

/** The same, for a set released together — see `closeLiveSearchesController`. */
export const closeLiveSearchSubscriptions = async (subscriptionIds: string[]): Promise<void> => {
    await loadsIngestServiceAxios.post('/loads/live-searches/close', { subscriptionIds })
}

/** Transform LoadSearchRequest into structured payload for /loads/search */
const buildPayload = (params: LoadSearchRequest) => ({
    sources: params.sources?.map((s) => s.toUpperCase().replace(/-/g, '_')),
    limit: params.limit,
    offset: params.offset,
    cursor: params.cursor,
    // Unified-endpoint flags (see docs/ADR/load-pipeline/ADR-040.4-Search-Aggregator.md):
    //   - usePersonalDatToken: Load Hub is the customer's own account → per-project
    //     AIHub DAT integration (preserves the legacy JWT-path behaviour, which the
    //     old /loads/search derived from auth mode).
    //   - count_against_quota: false → Load Hub meters its OWN search quota
    //     (consumeSearchQuota); the endpoint's per-user DAT-quota gate must not
    //     double-count this JWT caller.
    usePersonalDatToken: params.usePersonalDatToken ?? true,
    skipDatProcessing: params.skipDatProcessing ?? false,
    count_against_quota: false,
    origin: params.origin
        ? {
              city: params.origin.city,
              state: params.origin.state,
              states: params.origin.states,
              latitude: params.origin.latitude,
              longitude: params.origin.longitude,
              radiusMiles: params.origin.dhd,
          }
        : undefined,
    destination: params.destination
        ? {
              city: params.destination.city,
              state: params.destination.state,
              states: params.destination.states,
              latitude: params.destination.latitude,
              longitude: params.destination.longitude,
              radiusMiles: params.destination.dhd,
          }
        : undefined,
    earliestPickup: params.pickup?.earliestAvailability,
    latestPickup: params.pickup?.latestAvailability,
    earliestPickupIso: params.pickup?.earliestAvailabilityIso,
    latestPickupIso: params.pickup?.latestAvailabilityIso,
    pickupWindowFromIso: params.pickup?.fromIso,
    pickupWindowToIso: params.pickup?.toIso,
    unifiedEquipmentTypes: params.equipmentTypes,
    datEquipmentTypes: params.datEquipmentTypes,
    sylectusVehicleSizes: params.sylectusVehicleSizes,
    fullPartial: params.fullPartial,
    weight: params.weight,
    length: params.length,
    maxPostedAgeHours: params.maxPostedAgeHours,
    numeoId: params.numeoId,
    liveSearch: params.liveSearch,
    // Pooled-DAT usage attribution (`dat_token_usage`). Default rather than require: this
    // function serves exactly two boards, and only the Box Truck one overrides. Without a
    // default both would keep reporting as 'unknown', which is what made the biggest bucket
    // in the daily rollup the uninformative one.
    trigger: params.trigger ?? 'load_hub',
})

type SourceErrorKind = 'auth' | 'transient'

interface LiveSearchReady {
    subscriptionId: string
    searchId: string
    expiresAt: string
}

const isLiveSearchReady = (value: unknown): value is LiveSearchReady =>
    isRecord(value) && typeof value.subscriptionId === 'string' && typeof value.searchId === 'string' && typeof value.expiresAt === 'string'

/** Fetch BEON load detail by loadIdGuid */
export const getBeonLoadDetail = async (loadIdGuid: string): Promise<Partial<CanonicalLoad> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Partial<CanonicalLoad> }>(`/loads/loadboards/beon/loads/${loadIdGuid}`)
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** Fetch Navisphere load detail by shipment number */
export const getNavisphereLoadDetail = async (shipmentNumber: number): Promise<Partial<CanonicalLoad> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Partial<CanonicalLoad> }>(
            `/loads/loadboards/navisphere/loads/${shipmentNumber}`
        )
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** Fetch Emerge load detail by loadId */
export const getEmergeLoadDetail = async (loadId: number): Promise<Partial<CanonicalLoad> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Partial<CanonicalLoad> }>(`/loads/loadboards/emerge/loads/${loadId}`)
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** Fetch SPOT load contact details by shipmentId (UUID) */
export const getSpotLoadDetail = async (shipmentId: string): Promise<Partial<CanonicalLoad> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Partial<CanonicalLoad> }>(`/loads/loadboards/spot/loads/${shipmentId}`)
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** Fetch Schneider load contact detail by loadId */
export const getSchneiderLoadDetail = async (loadId: string): Promise<Partial<CanonicalLoad> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Partial<CanonicalLoad> }>(`/loads/loadboards/schneider/loads/${loadId}`)
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** Fetch Arrive load detail by loadNumber */
export const getArriveLoadDetail = async (loadNumber: number): Promise<Partial<CanonicalLoad> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Partial<CanonicalLoad> }>(`/loads/loadboards/arrive/loads/${loadNumber}`)
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** Fetch DAT load extra detail (pickup/drop hours, commodity, peer rating) by resultId */
interface DatLoadDetail {
    pickupHours: string | null
    dropOffHours: string | null
    commodity: string | null
    peerRatingSummary: { numOfRatings: number; averageRating: number } | null
    postingId: string | null
}

export const getDatLoadDetail = async (resultId: string): Promise<DatLoadDetail | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: DatLoadDetail }>('/loads/loadboards/dat/load-detail', {
            params: { resultId },
        })
        return response.data?.data ?? null
    } catch (err) {
        console.error('[getDatLoadDetail] failed', err)
        return null
    }
}

/** Fetch TruckStop Account load detail by load UUID (the suffix of `TRUCKSTOP_ACCOUNT|<uuid>` externalId) */
export const getTruckStopAccountLoadDetail = async (loadId: string): Promise<Partial<CanonicalLoad> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Partial<CanonicalLoad> }>(
            `/loads/loadboards/truckstop-account/loads/${loadId}`
        )
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** Fetch Sylectus load detail from LoadBid page */
export const getSylectusLoadDetail = async (
    pronumuk: string,
    pronum: string,
    loadmabcode: string,
    loadsf: string
): Promise<Record<string, string | null> | null> => {
    try {
        const response = await loadsIngestServiceAxios.get<{ success: boolean; data: Record<string, string | null> }>(
            '/loads/loadboards/sylectus/load-detail',
            {
                params: { pronumuk, pronum, loadmabcode, loadsf },
            }
        )
        return response.data?.data ?? null
    } catch {
        return null
    }
}

/** An externalId to resolve — a bare id (defaults to DAT server-side) or an explicit source pairing.
 *  Pass the pair form for non-DAT sources (e.g. SYLECTUS_EMAIL) so the PG-interactions composite matches. */
type EmailThreadIdInput = string | { externalId: string; sourceType: string }

/** Fetch email thread IDs and unread counts for a list of externalIds (Mongo + PG-interactions fallback) */
export const fetchEmailThreadIds = async (
    externalIds: EmailThreadIdInput[]
): Promise<Record<string, { emailThreadId: string; unreadCount: number; loadId?: string }>> => {
    if (!externalIds.length) return {}
    try {
        const response = await axios.post<{ success: boolean; data: Record<string, { emailThreadId: string; unreadCount: number; loadId?: string }> }>(
            '/ext/loads/email-threads',
            { externalIds }
        )
        return response.data?.data ?? {}
    } catch {
        return {}
    }
}

interface StreamingSearchOptions {
    /** Search over the search stream instead of `search-unified`: set for the users `featureFlags.boxTruckStreamSearch` names. */
    viaSearchStream?: boolean
}

interface StreamingSearchCallbacks {
    /** Progressive render hook — fires only for a source that delivered VISIBLE rows. */
    onSourceLoads: (source: string, loads: CanonicalLoad[]) => void
    /**
     * Per-source metadata from `source_complete`, delivered for EVERY completed source — including one whose
     * visible batch came back empty. Deliberately NOT folded into `onSourceLoads`, which is gated on a
     * non-empty batch: a DB page can come back FULL (`hasMore: true`) and still filter down to zero visible
     * rows app-side (deadhead over-radius, freshness), and routing the pagination signal through the
     * render hook would drop it exactly there — the board would stop paginating with rows still to fetch.
     * Mirrors the mobile client's `onSourceComplete`.
     *
     * `meta.hasMore` — the source's page came back FULL server-side, so more rows very likely remain. This is
     * the "load more" gate for an offset-paginating board; it replaced a grand-total count, which needed a
     * COUNT(*) OVER() across the whole matched set for a number no UI rendered.
     */
    onSourceComplete?: (source: string, meta: { hasMore?: boolean }) => void
    onSourceError: (
        source: string,
        error: string,
        errorKind?: SourceErrorKind,
        detail?: { code?: SourceErrorCode; reason?: SourceFailureReason; badInput?: boolean }
    ) => void
    /**
     * `pagedBy` says how the next page of THIS search is asked for: the search stream by `nextCursor` (null once it has no
     * more), `search-unified` by `offset`. A build with no stream host answers `offset` even for a user the flag names.
     */
    onComplete: (summary: {
        totalLoads: number
        uniqueLoads: number
        failedSources: FailedSource[]
        pagedBy: 'cursor' | 'offset'
        nextCursor: string | null
    }) => Promise<void> | void
    onLiveSearchReady?: (liveSearch: { subscriptionId: string; searchId: string; expiresAt: string }) => void
    signal?: AbortSignal
}

/**
 * Stream load search results via SSE — calls onSourceLoads as each source completes
 * so the UI can render loads progressively without waiting for all sources.
 */
export const searchLoadsStreaming = async (
    params: LoadSearchRequest,
    callbacks: StreamingSearchCallbacks,
    options: StreamingSearchOptions = {}
): Promise<void> => {
    const token = getAuthToken()

    const baseUrl = (loadsIngestServiceAxios.defaults.baseURL ?? '').replace(/\/$/, '')
    // The search stream (load-search-service, ADR-040.4) speaks `search-unified`'s frames, so the reader below is the same
    // for both. A build with no host for it keeps `search-unified`.
    const streamRoot = options.viaSearchStream ? getLoadSearchServiceUrl() : null
    const searchUrl = streamRoot ? `${streamRoot}${SEARCH_STREAM_PATH}` : `${baseUrl}/loads/search-unified`
    const payload = buildPayload(params)

    // Compose caller's cancellation signal with a hard 60s timeout so the stream
    // self-terminates if the server stalls mid-response and never sends all_complete.
    const timeoutSignal = AbortSignal.timeout(60_000)
    const signal = callbacks.signal ? AbortSignal.any([callbacks.signal, timeoutSignal]) : timeoutSignal

    // Unify Search Phase 3a: the primary (and only) search streams from the
    // canonical unified endpoint. Same SSE event contract as the legacy
    // /loads/search/stream, so the consumer below is unchanged.
    const response = await fetch(searchUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal,
    })

    if (!response.ok) {
        throw new SearchRequestFailed(response.status)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    const failedSources: FailedSource[] = []
    let totalLoads = 0
    let uniqueLoads = 0

    try {
        while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const parts = buffer.split('\n\n')
            buffer = parts.pop() ?? ''

            for (const part of parts) {
                const dataLine = part.split('\n').find((l) => l.startsWith('data:'))
                if (!dataLine) continue

                let event: Record<string, unknown>
                try {
                    event = JSON.parse(dataLine.slice(5).trim())
                } catch {
                    continue
                }

                if (event.type === 'source_complete') {
                    const rawLoads = (event.loads as Partial<CanonicalLoad>[]) ?? []
                    const loads = rawLoads.map((load, i) =>
                        normalizeLoadCoordinates({
                            ...load,
                            /**
                             * THE ID THE BOARD KEYS BY IS THE STRIPPED ONE, and the two arms disagreed about it.
                             *
                             * `search-unified` applies `stripLoad` before it answers, so its loads carry the raw
                             * `12345`. The search stream builds its loads with `toCanonicalLoad`, which writes
                             * `SOURCE|12345` — and every per-load overlay the board draws (driver bids, the team's
                             * bid emails, the rating, the email thread) is returned keyed by the RAW id. So on the
                             * stream arm every lookup missed: no red tint for a driver's bid, no blue or green for
                             * this desk's or a teammate's outreach, an empty Bids cell and no rating.
                             *
                             * Stripped here, at the one place both arms come through, rather than per consumer —
                             * `store-bid-loads.ts` had already had to do it for the Driver Bids board, and a third
                             * copy is how the next surface gets the same bug. A no-op for `search-unified`, whose
                             * ids carry no prefix, and for a load with no external id at all.
                             */
                            ...(load.externalId ? { externalId: stripExternalIdPrefix(load.externalId) } : {}),
                            id: load.id || -(i + 1),
                            firstSeenAt: load.firstSeenAt || new Date(),
                            lastSeenAt: load.lastSeenAt || new Date(),
                            pickupGeoJson: load.pickupGeoJson || null,
                            deliveryGeoJson: load.deliveryGeoJson || null,
                        })
                    ) as CanonicalLoad[]
                    totalLoads += loads.length
                    const hasMore = typeof event.hasMore === 'boolean' ? event.hasMore : undefined
                    if (loads.length > 0) {
                        callbacks.onSourceLoads(event.source as string, loads)
                    }
                    // Always — an empty visible batch still carries the pagination signal. See onSourceComplete.
                    callbacks.onSourceComplete?.(event.source as string, { hasMore })
                } else if (event.type === 'source_error') {
                    const source = event.source as string
                    const error = (event.error as string) || 'Unknown error'
                    const errorKind =
                        event.reason === 'token_expired' || event.reason === 'unauthorized' || event.reason === 'not_connected'
                            ? ('auth' as SourceErrorKind)
                            : ('transient' as SourceErrorKind)
                    const code = isSourceErrorCode(event.code) ? event.code : undefined
                    const reason = isSourceFailureReason(event.reason) ? event.reason : undefined
                    const badInput = event.errorKind === 'bad_input'
                    failedSources.push({ source, error, errorKind, code, reason, badInput })
                    callbacks.onSourceError(source, error, errorKind, { code, reason, badInput })
                } else if (event.type === 'live_search_ready') {
                    const liveSearch = event.liveSearch
                    if (isLiveSearchReady(liveSearch)) callbacks.onLiveSearchReady?.(liveSearch)
                } else if (event.type === 'all_complete') {
                    const summary = event.summary as { totalLoads?: number; uniqueLoads?: number; nextCursor?: string | null } | undefined
                    uniqueLoads = summary?.uniqueLoads ?? totalLoads
                    await callbacks.onComplete({
                        totalLoads: summary?.totalLoads ?? totalLoads,
                        uniqueLoads,
                        failedSources,
                        pagedBy: streamRoot ? 'cursor' : 'offset',
                        nextCursor: summary?.nextCursor ?? null,
                    })
                }
            }
        }
    } finally {
        reader.releaseLock()
    }
}
